import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth";
import { StatusBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { ROLE_LABEL, APP_TAGLINE, GPDP_LOGO, GPDP_LOGO_ALT } from "@/lib/constants";
import { firstName } from "@/lib/name";
import { GrowthSection } from "@/components/dashboard/GrowthSection";
import {
  Church, Users, ClipboardList, Handshake, Wallet, Coins, FileWarning,
  Inbox, Calendar as CalendarIcon, TrendingUp, AlertOctagon, Gavel, MapPin,
} from "lucide-react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";

function StatCard({ icon: Icon, label, value, hint, tone = "primary", testid }) {
  const toneMap = {
    primary: "bg-primary/10 text-primary border-primary/20",
    secondary: "bg-secondary/10 text-secondary border-secondary/20",
    info: "bg-sky-50 text-sky-800 border-sky-200",
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-900 border-amber-200",
    destructive: "bg-rose-50 text-rose-800 border-rose-200",
    gold: "bg-accent/15 text-accent-foreground border-accent/30",
  };
  return (
    <Card className="card-hover border-border/80 shadow-sm" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground font-medium">{label}</div>
            <div className="font-heading font-bold text-3xl mt-2 text-foreground">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
          </div>
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center border ${toneMap[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-3 mt-2">
      <h3 className="font-heading font-bold text-lg text-foreground">{children}</h3>
      {right}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [churches, setChurches] = useState([]);
  const [letters, setLetters] = useState([]);
  const [reports, setReports] = useState([]);
  const [leadership, setLeadership] = useState([]);
  const [klasisProfile, setKlasisProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, d, a, e, c, l, r, ld, kp] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/decisions"),
          api.get("/announcements"),
          api.get("/events"),
          api.get("/churches"),
          api.get("/letters/incoming").catch(() => ({ data: [] })),
          api.get("/reports"),
          api.get("/public/leadership").catch(() => ({ data: [] })),
          api.get("/klasis-profile").catch(() => ({ data: null })),
        ]);
        setSummary(s.data); setDecisions(d.data); setAnnouncements(a.data);
        setEvents(e.data); setChurches(c.data); setLetters(l.data); setReports(r.data);
        setLeadership(ld.data); setKlasisProfile(kp.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const role = user?.role;
  const isKetua = role === "KETUA_KLASIS" || role === "WAKIL_KETUA";
  const isSek = role === "SEKRETARIS_KLASIS" || role === "WAKIL_SEKRETARIS";
  const isBendahara = role === "BENDAHARA_KLASIS" || role === "WAKIL_BENDAHARA" || role === "BENDAHARA_JEMAAT";
  const isJemaat = role === "GEMBALA_JEMAAT" || role === "SEKRETARIS_JEMAAT";
  const myChurch = churches.find((c) => c.id === user?.church_id);

  return (
    <AppShell>
      <PWAInstallBanner />
      <div className="mb-6">
        <div className="text-xs tracking-[0.18em] uppercase text-secondary font-semibold">{ROLE_LABEL[role]} • {new Date().toLocaleDateString('id-ID',{weekday:'long', day:'numeric', month:'long', year:'numeric'})}</div>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{APP_TAGLINE}</p>
      </div>

      {/* Welcome banner — redesigned per brand v2 */}
      <div className="relative overflow-hidden rounded-2xl mb-8 bg-navy-hero text-white shadow-[0_20px_50px_-20px_rgba(23,54,93,0.5)]" data-testid="dashboard-welcome-banner">
        <div className="absolute inset-0 papua-motif opacity-[0.06]" />
        {/* Mountain silhouette bottom */}
        <div className="absolute bottom-0 inset-x-0 h-24 opacity-25 pointer-events-none hidden sm:block">
          <svg viewBox="0 0 1200 200" preserveAspectRatio="none" className="w-full h-full">
            <path d="M0,200 L0,120 L120,80 L240,140 L360,60 L520,120 L680,40 L820,110 L980,50 L1120,140 L1200,90 L1200,200 Z"
              fill="rgba(255,255,255,0.14)" />
          </svg>
        </div>

        {/* Decorative logo on right */}
        <div className="absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 pointer-events-none hidden md:block" data-testid="dashboard-logo-decoration">
          <div className="relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-52 w-52 lg:h-64 lg:w-64 rounded-full bg-[#E5B52A]/20 blur-3xl animate-pulse" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-40 w-40 lg:h-48 lg:w-48 rounded-full border-2 border-[#E5B52A]/40 animate-[spin_32s_linear_infinite]" style={{ borderStyle: "dashed" }} />
            </div>
            <div className="relative h-36 w-36 lg:h-44 lg:w-44 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-white/95 shadow-[0_20px_50px_rgba(0,0,0,0.35)]" />
              <img
                src={GPDP_LOGO}
                alt={GPDP_LOGO_ALT}
                className="relative h-28 w-28 lg:h-36 lg:w-36 object-contain"
                data-testid="dashboard-hero-logo"
              />
            </div>
          </div>
        </div>

        <div className="relative z-10 p-6 sm:p-10 max-w-2xl">
          <div className="flex items-center gap-3 md:hidden mb-4">
            <img src={GPDP_LOGO} alt={GPDP_LOGO_ALT} className="h-14 w-14 object-contain bg-white/95 rounded-full p-1.5 shadow-md" data-testid="dashboard-hero-logo-mobile" />
            <div className="h-8 w-px bg-white/25" />
            <div className="text-[0.65rem] tracking-[0.22em] uppercase font-semibold text-white/85">GPDP Klasis Mimika</div>
          </div>
          <div className="hidden md:flex items-center gap-3 mb-3">
            <div className="h-1 w-10 rounded-full bg-[#E5B52A]" />
            <div className="text-[0.7rem] tracking-[0.22em] uppercase font-semibold text-white/85">GPDP Klasis Mimika • Papua Tengah</div>
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight" data-testid="welcome-greeting">
            Shalom, {firstName(user?.name) || "Sahabat"}<span className="inline-block ml-2">👋</span>
          </h2>
          <p className="mt-3 text-sm sm:text-base text-white/85 max-w-lg">
            Selamat datang di <strong className="text-[#E5B52A]">SIMPELKAMI GPDP</strong>.<br className="hidden sm:block" />
            Satu Data • Satu Pelayanan • Satu Klasis
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/kalender"><Button size="sm" className="rounded-full bg-white text-primary hover:bg-white/90 font-semibold shadow-md" data-testid="btn-banner-agenda">Lihat Agenda</Button></Link>
            <Link to="/jemaat"><Button size="sm" variant="outline" className="rounded-full bg-transparent border-white/50 text-white hover:bg-white hover:text-primary" data-testid="btn-banner-jemaat">Lihat Jemaat</Button></Link>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : (
        <>
          {/* KPI Row - varies by role */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {isJemaat && myChurch ? (
              <>
                <StatCard icon={Church} label="Jemaat Saya" value={myChurch.short_name || myChurch.name} hint={myChurch.code} testid="stat-my-church" />
                <StatCard icon={ClipboardList} label="Status Data" value={<StatusBadge status={myChurch.verification_status || "UNVERIFIED"} />} tone="info" testid="stat-my-status" />
                <StatCard icon={Users} label="Total Jiwa" value={myChurch.jumlah_jiwa || "—"} hint={`${myChurch.jumlah_kk || "—"} KK`} tone="secondary" testid="stat-jiwa" />
                <StatCard icon={CalendarIcon} label="Agenda Terdekat" value={events.length} hint="Kegiatan mendatang" tone="warning" testid="stat-events" />
              </>
            ) : isBendahara ? (
              <>
                <StatCard icon={Wallet} label="Realisasi Anggaran" value="Rp 0" hint="Menunggu input data" tone="primary" testid="stat-budget" />
                <StatCard icon={Coins} label="Iuran Jemaat" value={`0/${summary?.total_churches || 8}`} hint="Lunas periode ini" tone="secondary" testid="stat-iuran" />
                <StatCard icon={TrendingUp} label="Penerimaan Bulan Ini" value="Rp 0" tone="success" testid="stat-penerimaan" />
                <StatCard icon={FileWarning} label="Bukti Belum Lengkap" value="0" tone="warning" testid="stat-bukti" />
              </>
            ) : (
              <>
                <StatCard icon={Church} label="Total Jemaat" value={summary?.total_churches ?? 0} hint={`${summary?.verified_churches || 0} terverifikasi`} testid="stat-churches" />
                <StatCard icon={MapPin} label="Pos PI" value={summary?.total_pos_pi ?? 0} hint="Unit rintisan" tone="warning" testid="stat-pos-pi" />
                <StatCard icon={Users} label="Total Jiwa Terverifikasi" value={(summary?.total_jiwa || 0).toLocaleString("id-ID")} hint={`${summary?.total_kk || 0} KK`} tone="secondary" testid="stat-jiwa" />
                <StatCard icon={ClipboardList} label="Laporan Masuk" value={`${summary?.reports_submitted || 0}/${summary?.reports_total || 0}`} hint="Periode berjalan" tone="info" testid="stat-reports" />
              </>
            )}
          </div>

          {/* Middle row */}
          <div className="mt-8">
            <GrowthSection />
          </div>

          {/* Laporan Periode chart tetap dipertahankan */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
            <Card className="lg:col-span-3">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base">Laporan Periode</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={reportChartData(reports)} dataKey="value" nameKey="label" innerRadius={40} outerRadius={70} paddingAngle={2}>
                        {reportChartData(reports).map((_, i) => (
                          <Cell key={i} fill={["hsl(var(--primary))","hsl(var(--secondary))","hsl(var(--muted-foreground))"][i]} />
                        ))}
                      </Pie>
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Content by role */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
            {/* Decisions / Follow-up */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3 flex-row items-center justify-between">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Gavel className="h-4 w-4 text-primary" /> Tindak Lanjut Keputusan
                </CardTitle>
                <Link to="/keputusan" className="text-xs text-primary hover:underline" data-testid="link-all-decisions">Lihat semua →</Link>
              </CardHeader>
              <CardContent className="space-y-3">
                {decisions.length === 0 ? (
                  <EmptyState label="Belum ada keputusan tercatat." />
                ) : decisions.slice(0, 5).map((d) => {
                  const isLate = d.due_date && d.due_date < new Date().toISOString().slice(0,10) && d.status !== "SELESAI";
                  return (
                    <div key={d.id} className="border border-border rounded-md p-3 hover:border-primary/50 transition-colors" data-testid={`decision-${d.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{d.title}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">
                            PIC: {d.pic || "—"} • Jatuh tempo: {d.due_date || "—"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isLate && <StatusBadge status="warning" label="TERLAMBAT" />}
                          <StatusBadge status={d.status} />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Progress value={d.progress || 0} className="h-1.5" />
                        <span className="text-[0.65rem] text-muted-foreground w-8 text-right">{d.progress || 0}%</span>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Announcements + Events */}
            <div className="space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-secondary" /> Agenda Terdekat
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {events.length === 0 ? <EmptyState label="Belum ada agenda." /> : events.slice(0,4).map((e) => (
                    <div key={e.id} className="flex items-start gap-3 py-1.5" data-testid={`event-${e.id}`}>
                      <div className="w-12 shrink-0 text-center">
                        <div className="text-[0.65rem] uppercase text-muted-foreground">{new Date(e.date).toLocaleDateString('id-ID',{month:'short'})}</div>
                        <div className="font-heading font-bold text-primary">{new Date(e.date).getDate()}</div>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{e.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{e.time || "—"} • {e.location || "—"}</div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="font-heading text-base">Pengumuman</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {announcements.length === 0 ? <EmptyState label="Belum ada pengumuman." /> : announcements.slice(0,3).map((a) => (
                    <div key={a.id} className="border-l-2 border-secondary pl-3" data-testid={`announcement-${a.id}`}>
                      <div className="text-sm font-medium">{a.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{a.body}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Sekretaris: letters row */}
          {isSek && (
            <Card className="mt-8">
              <CardHeader className="pb-3 flex-row items-center justify-between">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Inbox className="h-4 w-4 text-primary" /> Surat Masuk Terbaru
                </CardTitle>
                <Link to="/surat-masuk" className="text-xs text-primary hover:underline">Kelola →</Link>
              </CardHeader>
              <CardContent>
                {letters.length === 0 ? <EmptyState label="Belum ada surat masuk." /> : (
                  <div className="space-y-2">
                    {letters.slice(0,5).map((l) => (
                      <div key={l.id} className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{l.subject}</div>
                          <div className="text-xs text-muted-foreground truncate">{l.sender} • {l.letter_number}</div>
                        </div>
                        <StatusBadge status={l.status} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* BPK Klasis Structure */}
          {leadership.length > 0 && (
            <Card className="mt-8">
              <CardHeader className="pb-3 flex-row items-center justify-between">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" /> Struktur BPK Klasis Mimika
                  {klasisProfile?.periode_start && (
                    <span className="text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground ml-2">
                      Periode {new Date(klasisProfile.periode_start).getFullYear()}–{new Date(klasisProfile.periode_end).getFullYear()}
                    </span>
                  )}
                </CardTitle>
                <Link to="/pengurus" className="text-xs text-primary hover:underline" data-testid="link-all-pengurus">Lihat semua →</Link>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {leadership
                    .filter(p => p.organization === "BPK")
                    .map((p) => (
                      <div key={p.id} className="border border-border rounded-md p-3 hover:border-primary/30 transition-colors" data-testid={`bpk-${p.title.replace(/\s/g,'-')}`}>
                        <div className="text-[0.65rem] tracking-[0.18em] uppercase text-secondary">{p.title}</div>
                        <div className="font-medium text-sm mt-1">{p.person_name}</div>
                      </div>
                    ))}
                </div>
                {klasisProfile?.sk_bpk && (
                  <div className="mt-4 pt-4 border-t border-border text-[0.7rem] text-muted-foreground">
                    Ditetapkan berdasarkan SK Nomor <strong className="font-mono">{klasisProfile.sk_bpk}</strong> tanggal {klasisProfile.sk_bpk_date} oleh {klasisProfile.ketua_umum_sinode} (Ketua Umum BPHS-GPDP).
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Ketua: quick decisions overview */}
          {isKetua && (
            <Card className="mt-8 border-primary/20 bg-primary/5">
              <CardContent className="p-6">
                <div className="text-xs tracking-[0.18em] uppercase text-primary/80">Ringkasan Eksekutif</div>
                <div className="mt-2 font-heading text-primary text-lg">
                  {summary?.verified_churches || 0} dari {summary?.total_churches || 0} jemaat sudah terverifikasi datanya.
                  {summary?.decisions_late ? ` Ada ${summary.decisions_late} tindak lanjut yang melewati batas waktu.` : " Belum ada tindak lanjut yang terlambat."}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Data ditarik langsung dari database — bukan angka hardcoded.
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </AppShell>
  );
}

function EmptyState({ label }) {
  return <div className="text-xs text-muted-foreground italic py-4 text-center border border-dashed border-border rounded-md">{label}</div>;
}

function statusChartData(churches) {
  const map = { VERIFIED: 0, SUBMITTED: 0, REVISION_REQUIRED: 0, PARTIAL: 0, UNVERIFIED: 0 };
  churches.forEach((c) => { map[c.verification_status || "UNVERIFIED"] = (map[c.verification_status || "UNVERIFIED"] || 0) + 1; });
  return [
    { label: "Verified", value: map.VERIFIED },
    { label: "Submitted", value: map.SUBMITTED },
    { label: "Revisi", value: map.REVISION_REQUIRED },
    { label: "Partial", value: map.PARTIAL },
    { label: "Unverified", value: map.UNVERIFIED },
  ];
}

function reportChartData(reports) {
  const s = reports.filter(r => r.status === "SUBMITTED" || r.status === "ACCEPTED").length;
  const d = reports.filter(r => r.status === "DRAFT").length;
  const o = reports.length - s - d;
  return [
    { label: "Sudah Lapor", value: s },
    { label: "Draft", value: d },
    ...(o>0 ? [{ label: "Lainnya", value: o }] : []),
  ];
}
